import React, { useEffect, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import AdminShell from "../components/AdminShell";
import AppPage from "../components/AppPage";
import { adminStatsApi } from "../api";

const fmtDay = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}.${m}`;
};

function KpiCard({ label, value, sub, color = "primary" }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className={`text-${color} small text-uppercase fw-semibold mb-1`}>{label}</div>
        <div className="fs-3 fw-bold">{value ?? "—"}</div>
        {sub && <div className="small text-muted mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      setData(await adminStatsApi(days));
    } catch (e) {
      setErr(e?.response?.data?.detail || t("admin.load_error"));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const totals = data?.totals || {};
  const funnel = data?.funnel || {};

  return (
    <AdminShell title={t("admin.nav.dashboard")}>
      <AppPage
        bodyScroll="scroll"
        toolbar={(
          <>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="btn-group btn-group-sm">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              className={`btn ${days === d ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setDays(d)}
            >
              {d} {t("admin.days")}
            </button>
          ))}
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load}>
          <i className="bi bi-arrow-clockwise me-1"></i>
          {t("common.refresh")}
        </button>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}
          </>
        )}
      >

      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <KpiCard
            label={t("admin.kpi.visits")}
            value={totals.page_views}
            sub={`${t("admin.kpi.unique")}: ${totals.unique_sessions ?? 0}`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <KpiCard label={t("admin.kpi.registrations")} value={totals.registrations} color="success" />
        </div>
        <div className="col-6 col-lg-3">
          <KpiCard label={t("admin.kpi.companies")} value={totals.companies} color="info" />
        </div>
        <div className="col-6 col-lg-3">
          <KpiCard
            label={t("admin.kpi.feedback_new")}
            value={totals.feedback_new}
            sub={`${t("admin.kpi.total_companies")}: ${totals.companies_total ?? 0}`}
            color="warning"
          />
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("admin.chart.title")}</h6>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={data?.series || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={fmtDay} fontSize={11} />
                    <YAxis allowDecimals={false} fontSize={11} />
                    <Tooltip labelFormatter={(v) => v} />
                    <Legend />
                    <Line type="monotone" dataKey="visits" name={t("admin.kpi.visits")} stroke="#0b3d91" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="registrations" name={t("admin.kpi.registrations")} stroke="#198754" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="companies" name={t("admin.kpi.companies")} stroke="#0dcaf0" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("admin.funnel.title")}</h6>
              <div className="mb-2 d-flex justify-content-between">
                <span>{t("admin.funnel.visits")}</span>
                <strong>{funnel.visits ?? 0}</strong>
              </div>
              <div className="mb-2 d-flex justify-content-between">
                <span>{t("admin.funnel.registrations")}</span>
                <strong>{funnel.registrations ?? 0}</strong>
              </div>
              <div className="mb-3 d-flex justify-content-between">
                <span>{t("admin.funnel.companies")}</span>
                <strong>{funnel.companies ?? 0}</strong>
              </div>
              <div className="small text-muted">
                {t("admin.funnel.visit_to_reg")}: <strong>{funnel.visit_to_register_pct ?? 0}%</strong>
              </div>
              <div className="small text-muted">
                {t("admin.funnel.reg_to_company")}: <strong>{funnel.register_to_company_pct ?? 0}%</strong>
              </div>
              <hr />
              <div className="small text-muted">
                {t("admin.funnel.no_company")}: <strong>{totals.users_without_company ?? 0}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("admin.top_sources")}</h6>
              {(data?.top_sources || []).length === 0 ? (
                <p className="text-muted small mb-0">{t("admin.no_data")}</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {data.top_sources.map((row) => (
                    <li key={row.source} className="list-group-item d-flex justify-content-between px-0">
                      <span>{row.source}</span>
                      <span className="badge bg-primary rounded-pill">{row.visits}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("admin.top_pages")}</h6>
              {(data?.top_pages || []).length === 0 ? (
                <p className="text-muted small mb-0">{t("admin.no_data")}</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {data.top_pages.map((row) => (
                    <li key={row.path} className="list-group-item d-flex justify-content-between px-0">
                      <code className="small">{row.path}</code>
                      <span className="badge bg-secondary rounded-pill">{row.visits}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      </AppPage>
    </AdminShell>
  );
}
